import React, {
  ReactNode,
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
} from "react";
import { FragmentOf, readFragment, ResultOf } from "gql.tada";
import { SlideCarouselSectionFragment } from "@queries/fragments/SlideCarouselSectionFragment";
import { Button } from "@components/shared/core/Button";
import { useIsDesktop } from "@hooks/useIsDesktop";
import { normalizeVariant } from "@utils/textUtils";

type SlideCarouselData = ResultOf<typeof SlideCarouselSectionFragment>;
type SlideItem = NonNullable<
  NonNullable<SlideCarouselData["itemsCollection"]>["items"][number]
>;

export type RenderCardArgs = {
  item: SlideItem;
  index: number;
  totalItems: number;
  isActive: boolean;
  direction: "left" | "right";
  slideRef: (node: HTMLDivElement | null) => void;
  onMouseEnter: () => void;
  onMouseLeave: () => void;
};

type Props = {
  data: FragmentOf<typeof SlideCarouselSectionFragment>;
  renderCard: (args: RenderCardArgs) => ReactNode;
};

const ArrowSvg = () => (
  <svg
    xmlns="http://www.w3.org/2000/svg"
    width="17"
    height="16"
    fill="none"
    aria-hidden
  >
    <path
      d="m8.5 1 7 7-7 7M14.833 8H1.5"
      stroke="currentColor"
      strokeWidth="2"
      strokeLinecap="round"
      strokeLinejoin="round"
    />
  </svg>
);

const NavArrows = ({
  canGoBack,
  canGoForward,
  onBack,
  onForward,
  size = "md",
  className = "",
}: {
  canGoBack: boolean;
  canGoForward: boolean;
  onBack: () => void;
  onForward: () => void;
  size?: "sm" | "md";
  className?: string;
}) => {
  const sizeClass = size === "sm" ? "size-10" : "size-[42px]";
  return (
    <div className={`flex items-center gap-3 ${className}`}>
      <Button
        variant="light-on-blur"
        size="inherit"
        className={sizeClass}
        disabled={!canGoBack}
        onClick={onBack}
        aria-label="Previous slide"
      >
        <div className="rotate-180">
          <ArrowSvg />
        </div>
      </Button>
      <Button
        variant="light-on-blur"
        size="inherit"
        className={sizeClass}
        disabled={!canGoForward}
        onClick={onForward}
        aria-label="Next slide"
      >
        <ArrowSvg />
      </Button>
    </div>
  );
};

/**
 * Shared carousel for SlideCarouselSection entries.
 *
 * Handles: scroll-snap track, pointer drag-to-scroll, active-index tracking,
 * hover step-through on desktop, arrow navigation, and progress indicator
 * (numeric "X of Y" or trackbar driven by `progressStyle`).
 *
 * Card visuals are supplied by the caller via `renderCard`. The carousel is
 * card-style-agnostic; cards are responsible for their own widths and the
 * carousel measures `offsetLeft` to scroll to a target index.
 */
export const SlideCarouselSection = ({ data: dataRef, renderCard }: Props) => {
  const data = readFragment(SlideCarouselSectionFragment, dataRef);
  const items = useMemo(
    () =>
      data.itemsCollection?.items.filter(
        (i): i is SlideItem => i !== null
      ) ?? [],
    [data]
  );

  const progressStyle = normalizeVariant(data.progressStyle) ?? "numeric";
  const isTrackbar = progressStyle === "trackbar";

  const [activeIndex, setActiveIndex] = useState(0);
  const [visualIndex, setVisualIndex] = useState<number | null>(null);
  const [direction, setDirection] = useState<"left" | "right">("right");
  const [scrollProgress, setScrollProgress] = useState(0);
  const [isScrollable, setIsScrollable] = useState(false);
  const trackRef = useRef<HTMLDivElement>(null);
  const slideRefs = useRef<Array<HTMLDivElement | null>>([]);
  const isDesktop = useIsDesktop();

  const visualIndexRef = useRef<number | null>(null);
  const lastHoveredIndexRef = useRef<number | null>(null);
  const stepTimersRef = useRef<ReturnType<typeof setTimeout>[]>([]);
  const prevActiveIndex = useRef(0);

  const clearStepTimers = useCallback(() => {
    stepTimersRef.current.forEach(clearTimeout);
    stepTimersRef.current = [];
  }, []);

  const handleHoverEnter = useCallback(
    (targetIndex: number) => {
      clearStepTimers();

      const last = lastHoveredIndexRef.current;
      if (last !== null && last !== targetIndex) {
        setDirection(targetIndex > last ? "right" : "left");
      }

      const stepFrom = visualIndexRef.current ?? lastHoveredIndexRef.current;
      lastHoveredIndexRef.current = targetIndex;

      if (stepFrom !== null && Math.abs(targetIndex - stepFrom) > 1) {
        const step = targetIndex > stepFrom ? 1 : -1;
        const STEP_MS = 300;
        let delay = 0;
        for (let i = stepFrom + step; i !== targetIndex + step; i += step) {
          const idx = i;
          stepTimersRef.current.push(
            setTimeout(() => {
              visualIndexRef.current = idx;
              setVisualIndex(idx);
            }, delay)
          );
          delay += STEP_MS;
        }
      } else {
        visualIndexRef.current = targetIndex;
        setVisualIndex(targetIndex);
      }
    },
    [clearStepTimers]
  );

  const handleHoverLeave = useCallback(() => {
    clearStepTimers();
    visualIndexRef.current = null;
    setVisualIndex(null);
  }, [clearStepTimers]);

  // Active index from scroll position + scroll progress for trackbar.
  useEffect(() => {
    const track = trackRef.current;
    if (!track) return;

    const updateActive = () => {
      const scrollLeft = track.scrollLeft;
      const styles = window.getComputedStyle(track);
      const paddingLeft = Number.parseFloat(styles.paddingLeft || "0");
      let nearestIndex = 0;
      let nearestDistance = Number.POSITIVE_INFINITY;

      slideRefs.current.forEach((slide, index) => {
        if (!slide) return;
        const slideStart = slide.offsetLeft - paddingLeft;
        const distance = Math.abs(slideStart - scrollLeft);
        if (distance < nearestDistance) {
          nearestDistance = distance;
          nearestIndex = index;
        }
      });

      if (nearestIndex !== prevActiveIndex.current) {
        setDirection(
          nearestIndex > prevActiveIndex.current ? "right" : "left"
        );
        prevActiveIndex.current = nearestIndex;
      }
      setActiveIndex(nearestIndex);

      const maxScroll = track.scrollWidth - track.clientWidth;
      setIsScrollable(maxScroll > 0);
      setScrollProgress(maxScroll > 0 ? track.scrollLeft / maxScroll : 0);
    };

    let frame = 0;
    const onScroll = () => {
      cancelAnimationFrame(frame);
      frame = requestAnimationFrame(updateActive);
    };

    updateActive();
    track.addEventListener("scroll", onScroll, { passive: true });
    window.addEventListener("resize", updateActive);
    return () => {
      cancelAnimationFrame(frame);
      track.removeEventListener("scroll", onScroll);
      window.removeEventListener("resize", updateActive);
    };
  }, [items.length]);

  // Pointer drag-to-scroll.
  useEffect(() => {
    const track = trackRef.current;
    if (!track) return;
    let dragging = false;
    let dragStartX = 0;
    let dragScrollLeft = 0;

    const onMouseDown = (e: MouseEvent) => {
      dragging = true;
      dragStartX = e.pageX - track.offsetLeft;
      dragScrollLeft = track.scrollLeft;
      track.style.cursor = "grabbing";
      track.style.scrollSnapType = "none";
    };
    const onMouseMove = (e: MouseEvent) => {
      if (!dragging) return;
      e.preventDefault();
      const x = e.pageX - track.offsetLeft;
      track.scrollLeft = dragScrollLeft - (x - dragStartX);
    };
    const onMouseUp = () => {
      if (!dragging) return;
      dragging = false;
      track.style.cursor = "";
      track.style.scrollSnapType = "";
    };

    track.addEventListener("mousedown", onMouseDown);
    window.addEventListener("mousemove", onMouseMove);
    window.addEventListener("mouseup", onMouseUp);
    return () => {
      track.removeEventListener("mousedown", onMouseDown);
      window.removeEventListener("mousemove", onMouseMove);
      window.removeEventListener("mouseup", onMouseUp);
    };
  }, []);

  const scrollToIndex = (index: number) => {
    const track = trackRef.current;
    const target = slideRefs.current[index];
    if (!track || !target) return;
    const styles = window.getComputedStyle(track);
    const paddingLeft = Number.parseFloat(styles.paddingLeft || "0");
    track.scrollTo({
      left: target.offsetLeft - paddingLeft,
      behavior: "smooth",
    });
  };

  const displayIndex =
    isDesktop && visualIndex !== null ? visualIndex : activeIndex;
  const canGoBack = scrollProgress > 0;
  const canGoForward = scrollProgress < 1;

  const goBack = () => {
    if (!canGoBack) return;
    setDirection("left");
    scrollToIndex(activeIndex - 1);
  };
  const goForward = () => {
    if (!canGoForward) return;
    setDirection("right");
    scrollToIndex(activeIndex + 1);
  };

  if (items.length === 0) return null;

  return (
    <section
      aria-roledescription="carousel"
      aria-label={data.title ?? "Carousel"}
      className="pt-32 dsk:pt-48 pb-10 dsk:pb-20 overflow-hidden text-brand-cream"
    >
      {/* Header: title + description on the left, top-right gets progress or nav */}
      <div className="mb-5 dsk:mb-6">
        <div className="col-span-full flex flex-col min-[481px]:flex-row min-[481px]:items-end min-[481px]:justify-between gap-4">
          <div className="flex flex-col gap-[22px] max-w-[564px]">
            {data.title && (
              <h2 className="text-heading-3 min-[768px]:text-heading-2 text-balance">
                {data.title}
              </h2>
            )}
            {data.description && (
              <p className="text-neutral-cream text-body-regular tracking-[-0.01em] leading-[1.4]">
                {data.description}
              </p>
            )}
          </div>

          {isTrackbar
            ? isScrollable && (
                <div
                  className="hidden min-[481px]:block relative h-[3px] w-[100px] bg-neutral-cream/30 shrink-0 self-end"
                  aria-hidden
                >
                  <div
                    className="absolute inset-y-0 left-0 bg-neutral-cream transition-[width] duration-200"
                    style={{
                      width: `${Math.max(10, scrollProgress * 100)}%`,
                    }}
                  />
                </div>
              )
            : isScrollable && (
                <div
                  role="group"
                  aria-label="Slide controls"
                  className="flex items-center justify-end max-[480px]:justify-between gap-2 w-full min-[481px]:w-auto"
                >
                  <div className="text-body-small text-brand-cream/70 mr-1 whitespace-nowrap self-center">
                    <span aria-live="polite" className="sr-only">
                      Slide {displayIndex + 1} of {items.length}
                    </span>
                    <span aria-hidden="true">
                      {displayIndex + 1} of {items.length}
                    </span>
                  </div>
                  <NavArrows
                    canGoBack={canGoBack}
                    canGoForward={canGoForward}
                    onBack={goBack}
                    onForward={goForward}
                  />
                </div>
              )}
        </div>
      </div>

      {/* Track */}
      <div className="relative w-full overflow-hidden">
 
        <div
          ref={trackRef}
          className="flex gap-4 dsk:gap-8 overflow-x-auto snap-x snap-mandatory scroll-smooth pb-2 cursor-grab [scrollbar-width:none] [-ms-overflow-style:none] [&::-webkit-scrollbar]:hidden"
        >
          {items.map((item, index) => {
            const isActive = isDesktop
              ? visualIndex === index
              : activeIndex === index;
            return (
              <React.Fragment key={item.sys.id}>
                {renderCard({
                  item,
                  index,
                  totalItems: items.length,
                  isActive,
                  direction,
                  slideRef: (node) => {
                    slideRefs.current[index] = node;
                  },
                  onMouseEnter: () => isDesktop && handleHoverEnter(index),
                  onMouseLeave: () => isDesktop && handleHoverLeave(),
                })}
              </React.Fragment>
            );
          })}
        </div>
      </div>

      {/* Trackbar progressStyle puts arrows at the bottom-right. */}
      {isTrackbar && isScrollable && (
        <div className="mt-10 dsk:mt-12">
          <div className="flex justify-end">
            <NavArrows
              canGoBack={canGoBack}
              canGoForward={canGoForward}
              onBack={goBack}
              onForward={goForward}
            />
          </div>
        </div>
      )}
    </section>
  );
};
